import { useRef, useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ChevronLeft, ChevronRight } from "lucide-react"
import KPICard from "./KPICard"

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount || 0)

const formatNumber = (n) => new Intl.NumberFormat("en-US").format(Math.round(n) || 0)

const formatPercent = (n) => `${Math.round(n * 10) / 10}%`

// 6 KPIs in a snap-scroll carousel. No side padding on the wrapper — the
// track runs full-width, flush with Needs Your Attention / Learning Activity
// below it. Arrows sit as small overlay buttons right at the edge, with a
// short fade behind them so they read as an intentional control rather than
// a layout gap.
const KPIRow = ({ kpis, onNavigate }) => {
  const trackRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const updateScrollState = () => {
    const el = trackRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
  }

  useEffect(() => {
    updateScrollState()
    window.addEventListener("resize", updateScrollState)
    return () => window.removeEventListener("resize", updateScrollState)
  }, [kpis])

  const scrollByCard = (direction) => {
    const el = trackRef.current
    if (!el) return
    const card = el.querySelector("[data-kpi-card]")
    const cardWidth = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8
    el.scrollBy({ left: direction * cardWidth, behavior: "smooth" })
  }

  if (!kpis) return null

  const cards = [
    {
      key: "learners",
      label: "Total Learners",
      rawValue: kpis?.learners?.total || 0,
      formatValue: formatNumber,
      change: kpis?.learners?.change,
      sub: "vs previous period",
      icon: "Users",
      onClick: () => onNavigate?.("intelligence-learners"),
    },
    {
      key: "active",
      label: "Active Learners",
      rawValue: kpis?.activeLearners?.total || 0,
      formatValue: formatNumber,
      sub: `${kpis?.activeLearners?.pctOfTotal ?? 0}% of total learners`,
      icon: "Activity",
      onClick: () => onNavigate?.("intelligence-learners"),
    },
    {
      key: "products",
      label: "Products",
      rawValue: kpis?.products?.total || 0,
      formatValue: formatNumber,
      sub: `${kpis?.products?.published ?? 0} published`,
      icon: "Package",
      onClick: () => onNavigate?.("intelligence-commerce"),
    },
    {
      key: "revenue",
      label: "Revenue",
      rawValue: kpis?.revenue?.hasAnyOrders ? kpis?.revenue?.total || 0 : 0,
      formatValue: formatCurrency,
      change: kpis?.revenue?.hasAnyOrders ? kpis?.revenue?.change : undefined,
      sub: !kpis?.revenue?.hasAnyOrders ? "No orders yet" : undefined,
      icon: "DollarSign",
      onClick: () => onNavigate?.("intelligence-commerce"),
    },
    {
      key: "completion",
      label: "Completion Rate",
      rawValue: kpis?.completionRate?.value ?? 0,
      formatValue: formatPercent,
      change: kpis?.completionRate?.change,
      icon: "TrendingUp",
      onClick: () => onNavigate?.("intelligence-courses"),
    },
    {
      key: "credentials",
      label: "Credentials Issued",
      rawValue: kpis?.credentials?.total || 0,
      formatValue: formatNumber,
      change: kpis?.credentials?.change,
      icon: "Award",
      onClick: () => onNavigate?.("certificates"),
    },
  ]

  return (
    <div className="relative mb-6" style={{ perspective: "1200px" }}>
      {/* Left fade + arrow, overlaying the edge rather than insetting the track */}
            <div
        className={`hidden sm:block absolute inset-y-0 left-0 w-14 z-[5] bg-gradient-to-r from-background to-transparent pointer-events-none transition-opacity ${
          canScrollLeft ? "opacity-100" : "opacity-0"
        }`}
      />
      <button
        onClick={() => scrollByCard(-1)}
        disabled={!canScrollLeft}
        className="hidden sm:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 rounded-full bg-card border border-border shadow-md disabled:opacity-0 disabled:pointer-events-none hover:bg-muted transition-all"
        aria-label="Scroll left"
      >
        <ChevronLeft size={16} />
      </button>

      <div
        ref={trackRef}
        onScroll={updateScrollState}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden py-1"
      >
        {cards.map((card, i) => (
          <motion.div
            key={card.key}
            data-kpi-card
                        className="flex-none w-[75%] sm:w-[46%] lg:w-1/4 snap-start"
            initial={{ opacity: 0, rotateY: -12, y: 10 }}
            whileInView={{ opacity: 1, rotateY: 0, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            whileHover={{ rotateY: 4, rotateX: 2 }}
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: i * 0.03 }}
            style={{ transformStyle: "preserve-3d" }}
          >
            <KPICard {...card} />
          </motion.div>
        ))}
      </div>

                  {/* Right fade + arrow */}
      <div
        className={`hidden sm:block absolute inset-y-0 right-0 w-14 z-[5] bg-gradient-to-l from-background to-transparent pointer-events-none transition-opacity ${
          canScrollRight ? "opacity-100" : "opacity-0"
        }`}
      />
      <button
        onClick={() => scrollByCard(1)}
        disabled={!canScrollRight}
        className="hidden sm:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 rounded-full bg-card border border-border shadow-md disabled:opacity-0 disabled:pointer-events-none hover:bg-muted transition-all"
        aria-label="Scroll right"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}

export default KPIRow